/**
 * SyncModals.tsx
 *
 * Modal and toast components for Write Library:
 *
 * 1. SyncConflictModal — shown when the same novel was edited on two devices.
 * 2. SessionEndModal — psychology-powered exit moment with breadcrumb prompt.
 * 3. FrictionCheckIn — toast that appears after 3 min inactivity.
 * 4. FrictionNudgeToast — tailored nudge after 3x same friction reason.
 * 5. SyncStatus — small toolbar indicator for sync state.
 */

import React, { useState } from 'react';
import type { SyncConflict } from '../hooks/use-cloud-sync';
import type { SessionSummary, FrictionReason } from '../hooks/useWritingSession';

// ─── Shared modal shell ───────────────────────────────────────────────────────

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose?: () => void }) {
    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.45)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: '16px',
            }}
        >
            <div onClick={e => e.stopPropagation()}>{children}</div>
        </div>
    );
}

function ModalCard({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
    return (
        <div
            style={{
                background: 'var(--studio-paper, #FAF7F0)',
                border: '1px solid var(--studio-border, rgba(44,36,22,0.12))',
                borderRadius: '16px',
                padding: '36px',
                maxWidth: '460px',
                width: '100%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                color: 'var(--studio-text, #2C2416)',
                ...style,
            }}
        >
            {children}
        </div>
    );
}

function Btn({
    children,
    onClick,
    variant = 'primary',
    style,
}: {
    children: React.ReactNode;
    onClick: () => void;
    variant?: 'primary' | 'ghost' | 'danger';
    style?: React.CSSProperties;
}) {
    const base: React.CSSProperties = {
        padding: '10px 20px',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        fontFamily: 'sans-serif',
        fontWeight: 500,
        transition: 'opacity 0.15s',
        ...style,
    };

    const variants: Record<string, React.CSSProperties> = {
        primary: { background: 'var(--studio-accent, #8B7355)', color: '#fff' },
        ghost: { background: 'transparent', border: '1px solid var(--studio-border, rgba(44,36,22,0.2))', color: 'var(--studio-text, #2C2416)' },
        danger: { background: '#dc2626', color: '#fff' },
    };

    return (
        <button style={{ ...base, ...variants[variant] }} onClick={onClick}>
            {children}
        </button>
    );
}

// ─── 1. Sync Conflict Modal ───────────────────────────────────────────────────

interface SyncConflictModalProps {
    conflict: SyncConflict;
    onDismiss: () => void;
}

export function SyncConflictModal({ conflict, onDismiss }: SyncConflictModalProps) {
    const [resolving, setResolving] = useState(false);

    const handleAcceptServer = () => {
        conflict.acceptServer();
        onDismiss();
    };

    const handleKeepLocal = async () => {
        setResolving(true);
        await conflict.keepLocal();
        setResolving(false);
        onDismiss();
    };

    const serverDate = new Date(conflict.serverUpdatedAt).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });

    return (
        <ModalOverlay>
            <ModalCard>
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--studio-accent, #8B7355)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                </div>

                <h2 style={{ fontFamily: 'var(--studio-font, "Crimson Text", Georgia, serif)', fontSize: '22px', marginBottom: '8px', textAlign: 'center' }}>
                    Sync Conflict
                </h2>

                <p style={{ fontSize: '14px', lineHeight: 1.7, opacity: 0.7, textAlign: 'center', marginBottom: '28px', fontFamily: 'sans-serif' }}>
                    <strong style={{ opacity: 1 }}>{conflict.novelTitle}</strong> was edited on another device.
                    The server version was last updated {serverDate}. Which would you like to keep?
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                    <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid var(--studio-border, rgba(44,36,22,0.12))', fontSize: '13px', fontFamily: 'sans-serif', lineHeight: 1.6 }}>
                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>Server version</div>
                        <div style={{ opacity: 0.6 }}>Version {conflict.serverVersion}</div>
                        <div style={{ opacity: 0.6 }}>Updated {serverDate}</div>
                    </div>
                    <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid var(--studio-border, rgba(44,36,22,0.12))', fontSize: '13px', fontFamily: 'sans-serif', lineHeight: 1.6 }}>
                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>Your version</div>
                        <div style={{ opacity: 0.6 }}>Version {conflict.localVersion}</div>
                        <div style={{ opacity: 0.6 }}>Current device</div>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                    <Btn variant="ghost" onClick={handleAcceptServer} style={{ flex: 1 }}>
                        Use server version
                    </Btn>
                    <Btn variant="primary" onClick={handleKeepLocal} style={{ flex: 1 }}>
                        {resolving ? 'Saving...' : 'Keep my version'}
                    </Btn>
                </div>
            </ModalCard>
        </ModalOverlay>
    );
}

// ─── 2. Session End Modal ─────────────────────────────────────────────────────

interface SessionEndModalProps {
    summary: SessionSummary;
    onSaveBreadcrumb: (text: string) => void;
    onDismiss: () => void;
}

export function SessionEndModal({ summary, onSaveBreadcrumb, onDismiss }: SessionEndModalProps) {
    const [breadcrumb, setBreadcrumb] = useState('');
    const [step, setStep] = useState<'summary' | 'breadcrumb'>('summary');

    const handleFinish = () => {
        if (breadcrumb.trim()) {
            onSaveBreadcrumb(breadcrumb.trim());
        }
        onDismiss();
    };

    const minutes = Math.floor(summary.duration / 60);

    return (
        <ModalOverlay>
            <ModalCard>
                {step === 'summary' && (
                    <>
                        <div style={{ display: 'flex', gap: '16px', marginBottom: '28px', justifyContent: 'center' }}>
                            {summary.wordsAdded > 0 && (
                                <StatPill label="words" value={summary.wordsAdded.toLocaleString()} />
                            )}
                            {minutes > 0 && (
                                <StatPill label="minutes" value={String(minutes)} />
                            )}
                            {summary.streakDay > 0 && (
                                <StatPill label="day streak" value={String(summary.streakDay)} />
                            )}
                        </div>

                        <p style={{ fontFamily: 'var(--studio-font, "Crimson Text", Georgia, serif)', fontSize: '22px', lineHeight: 1.5, textAlign: 'center', marginBottom: '32px', fontStyle: 'italic' }}>
                            "{summary.message}"
                        </p>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <Btn variant="ghost" onClick={onDismiss}>Close</Btn>
                            <Btn variant="primary" onClick={() => setStep('breadcrumb')}>Leave a breadcrumb →</Btn>
                        </div>
                    </>
                )}

                {step === 'breadcrumb' && (
                    <>
                        <h2 style={{ fontFamily: 'var(--studio-font, "Crimson Text", Georgia, serif)', fontSize: '20px', marginBottom: '8px' }}>
                            Leave a breadcrumb for tomorrow
                        </h2>
                        <p style={{ fontSize: '13px', opacity: 0.6, marginBottom: '20px', fontFamily: 'sans-serif', lineHeight: 1.6 }}>
                            This will appear as ghost text when you open this chapter next time. Eliminate the blank page.
                        </p>

                        <textarea
                            autoFocus
                            value={breadcrumb}
                            onChange={e => setBreadcrumb(e.target.value)}
                            placeholder={summary.breadcrumbPrompt}
                            rows={3}
                            style={{
                                width: '100%',
                                padding: '12px',
                                borderRadius: '8px',
                                border: '1px solid var(--studio-border, rgba(44,36,22,0.2))',
                                background: 'var(--studio-bg, #F5F1E8)',
                                color: 'var(--studio-text, #2C2416)',
                                fontSize: '15px',
                                fontFamily: 'var(--studio-font, "Crimson Text", Georgia, serif)',
                                lineHeight: 1.6,
                                resize: 'vertical',
                                outline: 'none',
                                boxSizing: 'border-box',
                                marginBottom: '20px',
                            }}
                        />

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <Btn variant="ghost" onClick={onDismiss}>Skip</Btn>
                            <Btn variant="primary" onClick={handleFinish}>Save breadcrumb</Btn>
                        </div>
                    </>
                )}
            </ModalCard>
        </ModalOverlay>
    );
}

// ─── 3. Friction Journal Check-In ────────────────────────────────────────────

interface FrictionCheckInProps {
    onSubmit: (reason: FrictionReason) => void;
}

export function FrictionCheckIn({ onSubmit }: FrictionCheckInProps) {
    const options: { reason: FrictionReason; label: string; emoji: string }[] = [
        { reason: 'social_media', label: 'Social media', emoji: '📱' },
        { reason: 'self_doubt', label: 'Self-doubt', emoji: '🌫' },
        { reason: 'plot_problem', label: 'Plot problem', emoji: '🧩' },
        { reason: 'life_stuff', label: 'Life got in the way', emoji: '🌀' },
        { reason: 'flow_state_ended', label: 'Natural stopping point', emoji: '☕' },
        { reason: 'skip', label: 'Just a break', emoji: '💨' },
    ];

    return (
        <div style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            background: 'var(--studio-paper, #FAF7F0)',
            border: '1px solid var(--studio-border, rgba(44,36,22,0.12))',
            borderRadius: '14px',
            padding: '20px 24px',
            maxWidth: '300px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            zIndex: 1000,
            color: 'var(--studio-text, #2C2416)',
            animation: 'slideUp 0.3s ease',
        }}>
            <p style={{ fontFamily: 'sans-serif', fontSize: '13px', opacity: 0.7, marginBottom: '12px', lineHeight: 1.5 }}>
                Quick check-in — what pulled you away?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {options.map(opt => (
                    <button
                        key={opt.reason}
                        onClick={() => onSubmit(opt.reason)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            border: '1px solid var(--studio-border, rgba(44,36,22,0.1))',
                            background: 'transparent',
                            color: 'var(--studio-text, #2C2416)',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontFamily: 'sans-serif',
                            textAlign: 'left',
                        }}
                    >
                        <span>{opt.emoji}</span>
                        <span>{opt.label}</span>
                    </button>
                ))}
            </div>
            <style>{`
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
        </div>
    );
}

// ─── 4. Friction Nudge Toast ──────────────────────────────────────────────────

interface FrictionNudgeToastProps {
    message: string;
    onDismiss: () => void;
}

export function FrictionNudgeToast({ message, onDismiss }: FrictionNudgeToastProps) {
    return (
        <div style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            background: 'var(--studio-paper, #FAF7F0)',
            border: '1px solid var(--studio-border, rgba(44,36,22,0.12))',
            borderRadius: '14px',
            padding: '20px 24px',
            maxWidth: '320px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            zIndex: 1000,
            color: 'var(--studio-text, #2C2416)',
        }}>
            <p style={{ fontSize: '14px', lineHeight: 1.7, fontFamily: 'sans-serif', marginBottom: '16px' }}>
                {message}
            </p>
            <Btn variant="ghost" onClick={onDismiss} style={{ fontSize: '12px', padding: '6px 14px' }}>
                Got it
            </Btn>
        </div>
    );
}

// ─── 5. Sync Status Indicator ─────────────────────────────────────────────────

interface SyncStatusProps {
    status: 'idle' | 'syncing' | 'error' | 'offline';
}

export function SyncStatus({ status }: SyncStatusProps) {
    const configs = {
        idle: { color: '#22c55e', label: 'Saved' },
        syncing: { color: '#f59e0b', label: 'Syncing…' },
        error: { color: '#ef4444', label: 'Sync error' },
        offline: { color: '#94a3b8', label: 'Offline' },
    };

    const cfg = configs[status];

    return (
        <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12px',
            fontFamily: 'sans-serif',
            color: 'var(--studio-text, #2C2416)',
            opacity: 0.6,
        }}>
            <span style={{
                width: '7px',
                height: '7px',
                borderRadius: '50%',
                background: cfg.color,
                display: 'inline-block',
                animation: status === 'syncing' ? 'pulse 1.5s infinite' : 'none',
            }} />
            {cfg.label}
            <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
        </div>
    );
}

// ─── Helper: StatPill ─────────────────────────────────────────────────────────

function StatPill({ label, value }: { label: string; value: string }) {
    return (
        <div style={{
            padding: '12px 20px',
            borderRadius: '10px',
            background: 'var(--studio-bg, #F5F1E8)',
            border: '1px solid var(--studio-border, rgba(44,36,22,0.1))',
            textAlign: 'center',
            minWidth: '80px',
        }}>
            <div style={{ fontSize: '24px', fontFamily: 'var(--studio-font, "Crimson Text", Georgia, serif)', fontWeight: 600 }}>
                {value}
            </div>
            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', opacity: 0.55, marginTop: '2px' }}>
                {label}
            </div>
        </div>
    );
}
